import AllOutIcon from '@mui/icons-material/AllOut';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';
import GroupIcon from '@mui/icons-material/Group';
import { CircularProgress, IconButton, Theme, Tooltip } from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import {
  GridValueFormatterParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { CancelTokenSource } from 'axios';
import clsx from 'clsx';
import React from 'react';
import { generatePath, Link, NavLink } from 'react-router-dom';

import exelSVG from '../../../../../images/excel.svg';
import groupRemoveIcon from '../../../../../images/group_remove_black_24dp.svg';
import HashTagSVG from '../../../../../images/hashtag.svg';

const useStyles = makeStyles((theme: Theme) => ({
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  editIconButton: {
    color: theme.palette.primary.main,
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  flushIconButton: {
    color: 'green',
  },
  viewContactsButton: {
    color: 'green',
  },
  viewContactsButtonDisabled: {
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(1.5),
  },
  uploadStatusButtonDisabled: {
    marginLeft: theme.spacing(1.5),
    marginRight: theme.spacing(1.5),
    filter: 'grayscale(100%) brightness(1%) contrast(1%)',
  },
  actionButton: {
    whiteSpace: 'nowrap',
    flexDirection: 'column',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& article': {
      margin: 0,
    },
    '& p': {
      lineHeight: 'normal',
      whiteSpace: 'normal',
      textTransform: 'uppercase',
    },
  },
  fillerButton: {
    marginLeft: '87px',
    display: 'inline',
  },
  fillerButton2: {
    // marginLeft: '43.5px',
    display: 'inline',
  },
  noLeftIconMargin: {
    marginLeft: 0,
  },
}));

/**
 * Custom rendered row to be populated in DataGrid
 */
interface ActionColumnProps {
  /** For styling column cell */
  className?: string;
  /** Keep of which queue we are deleting */
  deleteItemId: string | number | undefined;
  /** Keep track of queue for deletion */
  setDeleteItemId: React.Dispatch<
    React.SetStateAction<string | number | undefined>
  >;
  /** Status of AJAX call */
  isLoading: boolean;
  /** Show the delete icon in the actions column */
  showDelete?: boolean;
  /** Handles queue  deletion */
  handleDeletion: (id: string | number) => void;
  /** Handles scheduled actions deletion */
  handleScheduledDeletion?: (id: string | number) => void;
  /** Callback to show deletion modal */
  editLink?: string;
  /** Callback to show delete modal */
  deleteLink?: string;
  /** Callback to show delete scheduled modal */
  deleteScheduledLink?: string;
  /** Axios cancel token used to cancel request (NOTE: cannot cancel deletion)  */
  deleteCancelTokenSource?: CancelTokenSource | undefined;
  /**  Handles queue flush */
  handleFlush?: ((id: string | number) => void) | undefined;
  handleNumbersLink?: string;
  renderAction?: (params: GridValueGetterParams) => React.ReactNode;
  setSelectedPowerlistId?: ((id: string | undefined) => void) | undefined;
  noFiller?: boolean;
  agentId?: string;
  profile?: boolean;
  noLeftIconMargin?: boolean; // Remove extra space on left side of buttons in actions column
  spreadSheetsLink?: string;
  setShowSpreadsheets?: any;
  hide?: (params: GridValueFormatterParams) => boolean;
  disableViewContacts?: boolean;
  disableUploadStatus?: boolean;
  disableDelete?: boolean;
}

// export const ActionColumn = (props: ActionColumnProps) => (
export const ActionColumn = ({
  showDelete = true,
  disableViewContacts = false,
  disableUploadStatus = false,
  disableDelete = false,
  ...props
}: ActionColumnProps) => (params: GridValueGetterParams) => {
  if (props?.hide?.(params)) {
    return <div></div>;
  }

  const classes = useStyles();
  const [flushSelected, setFlushSelected] = React.useState(false);

  const acceptHandler =
    flushSelected && props.handleFlush
      ? props.handleFlush
      : props.handleDeletion;
  const confirmTitle = flushSelected ? 'Confirm Flush' : 'Confirm Deletion';

  return (
    <div className={clsx(classes.actionButton, props.className)}>
      {props.deleteItemId === params.row.id ? (
        <article>
          {flushSelected ? (
            <div className={classes.fillerButton} />
          ) : (
            <div
              className={
                props.noLeftIconMargin
                  ? classes.noLeftIconMargin
                  : classes.fillerButton2
              }
            />
          )}
          <Tooltip title={confirmTitle}>
            <span>
              <IconButton
                aria-label="accept"
                onClick={(_event: React.MouseEvent<HTMLElement>) => {
                  acceptHandler(params.row.id);
                }}
                disabled={props.isLoading}
                size="large"
              >
                <CheckIcon color={props.isLoading ? 'disabled' : 'primary'} />
                {props.isLoading && (
                  <CircularProgress
                    size={43}
                    className={classes.buttonProgress}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Cancel">
            <span>
              <IconButton
                aria-label="cancel"
                onClick={(_event: React.MouseEvent<HTMLElement>) => {
                  // props.deleteCancelTokenSource && props.deleteCancelTokenSource.cancel(); // cancel if possible/applicable
                  props.setDeleteItemId(undefined);
                }}
                disabled={
                  props.isLoading // && !props.deleteCancelTokenSource
                }
                size="large"
              >
                <ClearIcon
                  color="disabled"
                  className={clsx({
                    [classes.deleteIconButton]: !props.isLoading, // || props.deleteCancelTokenSource,
                  })}
                />
              </IconButton>
            </span>
          </Tooltip>
          {/* place after article: <p>Delete is irreversible</p> */}
        </article>
      ) : (
        <article>
          {props.setSelectedPowerlistId && (
            <>
              {disableViewContacts ? (
                <GroupIcon
                  color="disabled"
                  className={classes.viewContactsButtonDisabled}
                />
              ) : (
                <Tooltip title="View Contacts">
                  <NavLink
                    to={generatePath(
                      props.agentId
                        ? props.profile
                          ? `/my-profile/${props.agentId}/powerlists/${params.row.powerlistId}/view-contacts`
                          : `/manage/agents/${props.agentId}/powerlists/${params.row.powerlistId}/view-contacts`
                        : `/manage/powerlists/${params.row.powerlistId}/view-contacts`
                    )}
                  >
                    <IconButton
                      data-pup={`view-powerlist-contacts`}
                      onClick={(_event: React.MouseEvent<HTMLElement>) => {
                        if (!props.setSelectedPowerlistId) {
                          return;
                        }
                        props.setSelectedPowerlistId(params.row.powerlistId);
                      }}
                      aria-label="view contacts"
                      size="large"
                    >
                      <GroupIcon className={classes.flushIconButton} />
                    </IconButton>
                  </NavLink>
                </Tooltip>
              )}
              {disableUploadStatus ? (
                <img
                  style={{ height: 20 }}
                  src={exelSVG}
                  className={classes.uploadStatusButtonDisabled}
                />
              ) : (
                <Tooltip title="Upload Status">
                  <NavLink
                    to={generatePath(
                      props.spreadSheetsLink as string,
                      params.row
                    )}
                  >
                    <IconButton
                      onClick={(_event: React.MouseEvent<HTMLElement>) => {
                        if (!props.setSelectedPowerlistId) {
                          return;
                        }
                        props.setSelectedPowerlistId(params.row.powerlistId);
                      }}
                      aria-label="Upload Status"
                      size="large"
                    >
                      {/* <GroupIcon className={classes.flushIconButton} /> */}
                      <img style={{ height: 20 }} src={exelSVG}></img>
                    </IconButton>
                  </NavLink>
                </Tooltip>
              )}
            </>
          )}
          {props.renderAction?.(params)}
          {props.handleNumbersLink && (
            <Tooltip title="Numbers">
              <NavLink to={generatePath(props.handleNumbersLink, params.row)}>
                <IconButton aria-label="Numbers" size="large">
                  <img alt="Numbers" style={{ height: 16 }} src={HashTagSVG} />
                </IconButton>
              </NavLink>
            </Tooltip>
          )}
          {!props.noFiller ? (
            <Tooltip title="Edit">
              <NavLink to={generatePath(props.editLink!, params.row)}>
                <IconButton name="edit" size="large">
                  <CreateIcon className={classes.editIconButton} />
                </IconButton>
              </NavLink>
            </Tooltip>
          ) : (
            <div className={classes.fillerButton2} />
          )}
          {Boolean(
            props.deleteScheduledLink && props.handleScheduledDeletion
          ) && (
            <Tooltip title="Cancel Upcoming Cadence Events">
              <span>
                {props.deleteScheduledLink ? (
                  // if both link and delet handler passed
                  props.handleScheduledDeletion ? (
                    <IconButton
                      onClick={(_event: React.MouseEvent<HTMLElement>) => {
                        if (!props.handleScheduledDeletion) {
                          return;
                        }
                        setFlushSelected(false);
                        props.handleScheduledDeletion(params.row.id);
                      }}
                      aria-label="deleteScheduled"
                      size="large"
                    >
                      <img style={{ height: 20 }} src={groupRemoveIcon}></img>
                    </IconButton>
                  ) : (
                    <IconButton
                      component={Link}
                      to={generatePath(props.deleteScheduledLink, params.row)}
                      aria-label="deleteScheduled"
                      size="large"
                    >
                      <img style={{ height: 20 }} src={groupRemoveIcon}></img>
                    </IconButton>
                  )
                ) : (
                  <IconButton
                    aria-label="deleteScheduled"
                    onClick={(_event: React.MouseEvent<HTMLElement>) => {
                      setFlushSelected(false);
                      props.setDeleteItemId(params.row.id);
                    }}
                    disabled={props.isLoading || !showDelete}
                    size="large"
                  >
                    <img style={{ height: 20 }} src={groupRemoveIcon}></img>
                  </IconButton>
                )}
              </span>
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <span>
              {props.deleteLink ? (
                // if both link and delet handler passed
                props.handleDeletion ? (
                  <IconButton
                    onClick={(_event: React.MouseEvent<HTMLElement>) => {
                      setFlushSelected(false);
                      props.handleDeletion(params.row.id);
                    }}
                    aria-label="delete"
                    size="large"
                    disabled={disableDelete}
                  >
                    <DeleteIcon
                      className={clsx({
                        // enable styling only when not disabled
                        [classes.deleteIconButton]: !disableDelete,
                      })}
                    />
                  </IconButton>
                ) : (
                  <IconButton
                    component={Link}
                    to={generatePath(props.deleteLink, params.row)}
                    aria-label="delete"
                    size="large"
                    disabled={disableDelete}
                  >
                    <DeleteIcon
                      className={clsx({
                        // enable styling only when not disabled
                        [classes.deleteIconButton]: !disableDelete,
                      })}
                    />
                  </IconButton>
                )
              ) : (
                <IconButton
                  aria-label="delete"
                  onClick={(_event: React.MouseEvent<HTMLElement>) => {
                    setFlushSelected(false);
                    props.setDeleteItemId(params.row.id);
                  }}
                  disabled={props.isLoading || !showDelete || disableDelete}
                  size="large"
                >
                  {showDelete && (
                    <DeleteIcon
                      color="disabled"
                      className={clsx({
                        // enable styling only when not loading
                        [classes.deleteIconButton]:
                          !props.isLoading && !disableDelete,
                      })}
                    />
                  )}
                </IconButton>
              )}
            </span>
          </Tooltip>
          {props.handleFlush && (
            <Tooltip title="Flush">
              <span>
                <IconButton
                  className="superStyle"
                  onClick={(_event: React.MouseEvent<HTMLElement>) => {
                    if (!props.handleFlush) {
                      return;
                    }
                    setFlushSelected(true);
                    props.setDeleteItemId(params.row.id);
                  }}
                  aria-label="flush"
                  size="large"
                >
                  <AllOutIcon className={classes.flushIconButton} />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </article>
      )}
    </div>
  );
};
